import React from 'react';
import {
  IconButton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
  TableCaption,
  Box,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Heading,
  VStack,
  HStack,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { CopyIcon, InfoIcon } from '@chakra-ui/icons';
import * as _ from 'lodash';

import {
  Markups,
  PricingQuotePriceOutput,
  SubtotalsOutput,
  ClientType,
  ClientBudget,
  ClientComplexity,
  ResearchComplexity,
  UniverseType,
  PricingBreakdownRow,
} from '../../../lib/pricing-tool-api';

import { copyRichContent } from './utils';

export function pricingQuotePriceToString(
  numberOfQuestions: number,
  sampleSize: number,
) {
  return `${sampleSize} respondent, ${numberOfQuestions} question poll`;
}

export function priceString(price: number | undefined) {
  const num = price ?? 0;
  return num === 0 ? '-' : `$${_.round(num, 2).toLocaleString('en-US')}`;
}

export function mmoeString(mmoe: number | undefined): string {
  const num = mmoe ?? 0;
  return num === 0 ? '-' : `±${(num * 100).toFixed(1)}%`;
}

export interface ProposalListSectionProps {
  quotes: PricingQuotePriceOutput[];
  calculatedMaxRecommendedSampleSize: number;
  clientType: ClientType;
  clientBudget: ClientBudget;
  clientComplexity: ClientComplexity;
  researchComplexity: ResearchComplexity;
  universeTypes: UniverseType[];
  numberOfQuestions: number[];
  sampleSizes: number[];
}

interface PriceDetails {
  subtotals: SubtotalsOutput;
  price: number;
  roundedPrice: number;
  markups: Markups;
}

function SubtotalTable({
  subtotals,
  markups,
}: {
  subtotals: SubtotalsOutput;
  markups: Markups;
}) {
  return (
    <Table>
      <Thead>
        <Tr>
          <Th />
          <Th>Amount</Th>
        </Tr>
      </Thead>
      <Tbody>
        <Tr>
          <Td>Baseline Labor Cost</Td>
          <Td>{priceString(subtotals.baseline_labor_cost)}</Td>
        </Tr>
        <Tr>
          <Td>Labor Cost After Factors</Td>
          <Td>{priceString(subtotals.labor_cost)}</Td>
        </Tr>
        <Tr>
                        <Td>Budget Cost After Factors</Td>
              <Td>{priceString(subtotals.budget_cost)}</Td>
        </Tr>
        <Tr>
          <Td>Recruitment Cost</Td>
          <Td>{priceString(subtotals.recruitment_cost)}</Td>
        </Tr>
        <Tr>
          <Td>Labor + Recruitment</Td>
          <Td>{priceString(subtotals.labor_plus_recruitment)}</Td>
        </Tr>
        <Tr>
          <Td>Additional Labor Cost</Td>
          <Td>{priceString(subtotals.additional_labor_cost)}</Td>
        </Tr>
        <Tr>
          <Td>Video Markup</Td>
          <Td>{priceString(markups.video_markup)}</Td>
        </Tr>
        <Tr>
          <Td>Price Before Adjustment</Td>
          <Td>{priceString(subtotals.price_before_adjustment)}</Td>
        </Tr>
        <Tr>
          <Td>Total Adjustment</Td>
          <Td>{priceString(subtotals.total_adjustment)}</Td>
        </Tr>
      </Tbody>
    </Table>
  );
}

function FactorsTable({
  subtotals,
  markups,
  roundedPrice,
}: {
  subtotals: SubtotalsOutput;
  markups: Markups;
  roundedPrice: number;
}) {
  const { pricing_breakdown: pricingBreakdown } = subtotals;
  
  // Use the API-provided breakdown rows
  const formulaRows = pricingBreakdown.breakdown_rows;
  
  // Group rows by category
  const categories = [...new Set(formulaRows.map(row => row.category))];

  return (
    <TableContainer>
      <Table size="sm">
        <Thead>
          <Tr>
            <Th textAlign="left" fontWeight="bold" width="30%">
              Component
            </Th>
            <Th textAlign="left" fontWeight="bold" width="45%">
              Calculation
            </Th>
            <Th textAlign="right" fontWeight="bold" width="25%">
              Value
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {categories.map((category) => (
            <React.Fragment key={category}>
              <Tr>
                <Td colSpan={3} bg="blue.600" py={3}>
                  <Text fontWeight="bold" fontSize="md" color="white" textAlign="center">
                    {category}
                  </Text>
                </Td>
              </Tr>
                             {formulaRows
                 .filter(row => row.category === category)
                 .map((row, index) => (
                   <Tr key={`${category}-${row.description}`} bg={index % 2 === 0 ? "white" : "gray.50"}>
                    <Td textAlign="left">
                      <Text 
                        fontSize="sm" 
                        fontWeight={row.is_bold ? "bold" : "normal"}
                      >
                        {row.description}
                      </Text>
                    </Td>
                    <Td textAlign="left">
                      <Text fontSize="sm" color="gray.600">{row.calculation}</Text>
                    </Td>
                    <Td textAlign="right">
                      <Text 
                        fontSize="sm" 
                        fontWeight={row.is_bold ? "bold" : "semibold"}
                      >
                        {row.value}
                      </Text>
                    </Td>
                  </Tr>
                ))}
            </React.Fragment>
          ))}


        </Tbody>
      </Table>
    </TableContainer>
  );
}

export function PriceDetailModal({
  isOpen,
  onClose,
  priceDetails,
  clientType,
  clientBudget,
  clientComplexity,
  researchComplexity,
  universeTypes,
  questionCount,
  sampleSize,
}: {
  isOpen: boolean;
  onClose: () => void;
  priceDetails: PriceDetails | undefined;
  clientType: ClientType;
  clientBudget: ClientBudget;
  clientComplexity: ClientComplexity;
  researchComplexity: ResearchComplexity;
  universeTypes: UniverseType[];
  questionCount: number;
  sampleSize: number;
}) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader textAlign="center">Price Details</ModalHeader>
        <ModalBody>
          {priceDetails === undefined ? (
            <Box>Price details not available</Box>
          ) : (
            <FactorsTable
              subtotals={priceDetails.subtotals}
              markups={priceDetails.markups}
              roundedPrice={priceDetails.roundedPrice}
            />
          )}
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export function ProposalListSection(props: ProposalListSectionProps) {
  const {
    quotes,
    calculatedMaxRecommendedSampleSize,
    clientType,
    clientBudget,
      clientComplexity,
  researchComplexity,
    universeTypes,
    numberOfQuestions,
    sampleSizes,
  } = props;
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [modalPriceDetails, setModalPriceDetails] = React.useState<
    PriceDetails | undefined
  >();

  const handleQuoteInfoBtnClick = (priceDetails: PriceDetails) => {
    setModalPriceDetails(priceDetails);
    onOpen();
  };

  const tableRef = React.useRef<HTMLDivElement>(null);

  return (
    <Box>
      <TableContainer ref={tableRef}>
        <Table size="sm" variant="striped">
          <TableCaption
            placement="top"
            backgroundColor="brand.tableTitleColor"
            textColor="brand.bodyText"
            fontSize="lg"
          >
            Proposal List
            <IconButton
              aria-label="Copy proposals to clipboard"
              variant="ghost"
              color="brand.linkColor"
              marginLeft={10}
              onClick={() => {
                toast({
                  title: 'Proposal List',
                  description: 'Copied proposal list to clipboard!',
                  status: 'info',
                  duration: 5000,
                  isClosable: true,
                });
                copyRichContent(tableRef);
              }}
            >
              <CopyIcon />
            </IconButton>
          </TableCaption>
          <Thead>
            <Tr>
              <Th textAlign="center" fontWeight="bold">
                Description
              </Th>
              <Th textAlign="center" fontWeight="bold">
                Price
              </Th>
              <Th textAlign="center" fontWeight="bold">
                Discount (Included in Price)
              </Th>
              <Th textAlign="center" fontWeight="bold">
                Approximate Modeled MOE{' '}
                <Tooltip
                  placement="top"
                  hasArrow
                  label="This is the estimated modeled margin of error using a design effect of 1.29, which was the 90th percentile design effect in 2025 polls."
                >
                  <InfoIcon boxSize={3} />
                </Tooltip>
              </Th>
              <Th />
            </Tr>
          </Thead>
          <Tbody>
            {quotes.map(
              ({
                number_of_questions,
                sample_size,
                price,
                subtotals,
                rounded_price,
                markups,
                modeled_margin_of_error,
              }) => {
                const descriptor = pricingQuotePriceToString(
                  number_of_questions,
                  sample_size,
                );
                return (
                  <Tr
                    key={descriptor}
                    color={
                      sample_size > calculatedMaxRecommendedSampleSize
                        ? 'emphasisTextColor'
                        : 'current'
                    }
                  >
                    <Td textAlign="center">
                      <strong>{`${descriptor}`}</strong>
                    </Td>
                    <Td textAlign="center">{priceString(rounded_price)}</Td>
                    {/* only showing the adjustment when it is a discount */}
                    <Td textAlign="center">
                      {subtotals.total_adjustment < 0
                        ? priceString(Math.abs(subtotals.total_adjustment))
                        : priceString(0)}
                    </Td>
                    <Td textAlign="center">{mmoeString(modeled_margin_of_error)}</Td>
                    <Td>
                      <IconButton
                        aria-label="quote-info-btn"
                        color="black"
                        onClick={() =>
                          handleQuoteInfoBtnClick({
                            subtotals,
                            price,
                            roundedPrice: rounded_price,
                            markups,
                          })
                        }
                      >
                        <InfoIcon />
                      </IconButton>
                    </Td>
                  </Tr>
                );
              },
            )}
          </Tbody>
        </Table>
      </TableContainer>
      
      <PriceDetailModal
        isOpen={isOpen}
        onClose={onClose}
        priceDetails={modalPriceDetails}
        clientType={clientType}
        clientBudget={clientBudget}
        clientComplexity={clientComplexity}
        researchComplexity={researchComplexity}
        universeTypes={universeTypes}
        questionCount={0} // Will be set when modal opens
        sampleSize={0} // Will be set when modal opens  
      />
    </Box>
  );
}
